export const WIZARD_STORAGE_KEY = "stravo_new_property_wizard";
export const WIZARD_VERSION = 2;

export type PropertyTypeSlug =
  | "house"
  | "condo"
  | "villa"
  | "apartment"
  | "townhouse"
  | "studio"
  | "other";

export type ListingScanPhotoPersisted = { url: string; roomName: string };

export interface WizardState {
  wizardVersion?: number;
  step: number;
  propertyId: string | null;
  name: string;
  propertyType: PropertyTypeSlug;
  address: string;
  unit: string;
  city: string;
  state: string;
  country: string;
  coverPhotoUrl?: string | null;
  airbnbUrl: string;
  airbnbImportDone: boolean;
  /** JSON array of photos to scan after Airbnb import (step 2b). */
  listingScanPhotosJson: string;
  listingScanSkipped: boolean;
  listingScanDone: boolean;
  /** JSON PendingWizardProofStandard[] — applied when default checklist is created. */
  pendingProofStandardsJson: string;
  /** JSON PendingWizardCleaningTask[] — cleaning checklist tasks from AI scan, flushed at checklist step. */
  pendingChecklistTasksJson: string;
  rooms: string[];
  selectedTemplate: string | null;
  customTasksJson: string;
  checklistDone: boolean;
  checklistSkipped: boolean;
  checklistTaskCount: number;
  inventoryDone: boolean;
  inventorySkipped: boolean;
  inventoryItemCount: number;
  inventorySelectionsJson: string;
  icalUrl: string;
  calendarDone: boolean;
  calendarSkipped: boolean;
  inviteEmail: string;
  inviteRole: "cleaner" | "company_admin";
  teamDone: boolean;
  teamSkipped: boolean;
  teamInviteCount: number;
}

export const INITIAL_WIZARD_STATE: WizardState = {
  step: 1,
  propertyId: null,
  name: "",
  propertyType: "house",
  address: "",
  unit: "",
  city: "",
  state: "",
  country: "",
  coverPhotoUrl: null,
  airbnbUrl: "",
  airbnbImportDone: false,
  listingScanPhotosJson: "",
  listingScanSkipped: false,
  listingScanDone: false,
  pendingProofStandardsJson: "[]",
  pendingChecklistTasksJson: "[]",
  rooms: [],
  selectedTemplate: null,
  customTasksJson: "",
  checklistDone: false,
  checklistSkipped: false,
  checklistTaskCount: 0,
  inventoryDone: false,
  inventorySkipped: false,
  inventoryItemCount: 0,
  inventorySelectionsJson: "",
  icalUrl: "",
  calendarDone: false,
  calendarSkipped: false,
  inviteEmail: "",
  inviteRole: "cleaner",
  teamDone: false,
  teamSkipped: false,
  teamInviteCount: 0,
};

export function loadWizardState(): WizardState {
  try {
    const raw = localStorage.getItem(WIZARD_STORAGE_KEY);
    if (!raw) return { ...INITIAL_WIZARD_STATE };
    const parsed = JSON.parse(raw) as Partial<WizardState>;
    const propertyType = parsed.propertyType;
    const validTypes: PropertyTypeSlug[] = [
      "house",
      "condo",
      "villa",
      "apartment",
      "townhouse",
      "studio",
      "other",
    ];
    const safeType =
      propertyType && validTypes.includes(propertyType as PropertyTypeSlug)
        ? (propertyType as PropertyTypeSlug)
        : INITIAL_WIZARD_STATE.propertyType;
    let step = Math.min(9, Math.max(1, parsed.step ?? 1));
    const version = parsed.wizardVersion ?? 1;
    if (version < WIZARD_VERSION && step >= 3) {
      step = Math.min(9, step + 1);
    }
    return {
      ...INITIAL_WIZARD_STATE,
      ...parsed,
      propertyType: safeType,
      wizardVersion: WIZARD_VERSION,
      step,
      listingScanPhotosJson: parsed.listingScanPhotosJson ?? "",
      listingScanSkipped: parsed.listingScanSkipped ?? false,
      listingScanDone: parsed.listingScanDone ?? false,
      pendingProofStandardsJson: parsed.pendingProofStandardsJson ?? "[]",
      pendingChecklistTasksJson: parsed.pendingChecklistTasksJson ?? "[]",
    };
  } catch {
    return { ...INITIAL_WIZARD_STATE };
  }
}

export function saveWizardState(state: WizardState): void {
  localStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(state));
}

export function clearWizardState(): void {
  localStorage.removeItem(WIZARD_STORAGE_KEY);
}

export const WIZARD_STEP_LABELS = [
  "Basics",
  "Rooms & Photos",
  "Scan items",
  "Rooms",
  "Checklist",
  "Inventory",
  "Calendar",
  "Team",
  "Done",
] as const;

export function parseListingScanPhotos(json: string): ListingScanPhotoPersisted[] {
  if (!json.trim()) return [];
  try {
    const arr = JSON.parse(json) as ListingScanPhotoPersisted[];
    if (!Array.isArray(arr)) return [];
    return arr.filter((p) => p?.url && p?.roomName);
  } catch {
    return [];
  }
}

export function getDefaultRoomsForPropertyType(type: PropertyTypeSlug): string[] {
  switch (type) {
    case "house":
    case "villa":
    case "townhouse":
      return [
        "Living Room",
        "Kitchen",
        "Master Bedroom",
        "Master Bathroom",
        "Guest Bedroom",
        "Guest Bathroom",
        "Laundry",
      ];
    case "condo":
    case "apartment":
    case "studio":
      return ["Living Room", "Kitchen", "Bedroom", "Bathroom"];
    default:
      return [];
  }
}

export type ScrapedPhoto = { url: string; roomLabel: string | null };
